import React from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import {
  FEATURE_FLAGS,
  LaunchDarklyClientWithFlags,
  withLaunchDarklyConsumerHOC,
} from "shared-services";
import { RouteComponentProps, withRouter } from "react-router-dom";
import {
  getNavHeaderLinks,
  IMainNavigationItemProps,
  INavigation,
  INavigationHeaderProps,
  ISubNavigationItemProps,
  LocalizationUtils,
  myAccountLink,
  NavigationHeader,
} from "component-library";
import { AddOnEntitlements, PremiumEntitlements } from "shared-types";
import {
  doesUserHaveEntitlements,
  getUserGroup,
  IWithAuthProps,
  oidcLogout,
  withAuth,
} from "shared-auth";
import { withTranslation, WithTranslation } from "i18n";
import { IUser } from "shared/models/user";
import { navigator } from "shared/services/navigator";
import userService from "shared/services/userService";
import { MapDispatchToProps } from "shared/types/utilities";
import { BASE_PATH } from "config/common";
import config from "config/global";
import { IApplicationState } from "store";
import { hidePopup, setLocale, showPopup } from "store/ui/ui.actions";

import { HidePopup, SetLocale, ShowPopup } from "store/ui/ui.types";

import { routeTesters } from "./HeaderContainer.helpers";
import "./HeaderContainer.scss";

type LocalizationChangeHandler =
  INavigationHeaderProps["handlers"]["onLocalizationChangedHandler"];

export interface IHeaderContainerActions {
  showPopup: ShowPopup;
  hidePopup: HidePopup;
  setLocale: SetLocale;
}

export interface IHeaderContainerStoreProps {
  user: IUser;
  locale: string;
  isLocalizationVisible: boolean;
}

export interface IContainerProps {
  menuContainer: React.RefObject<HTMLDivElement>;
  popupContainer: React.RefObject<HTMLDivElement>;
}

export type IHeaderContainerProps = IHeaderContainerStoreProps &
  IHeaderContainerActions &
  RouteComponentProps &
  IContainerProps &
  LaunchDarklyClientWithFlags &
  WithTranslation &
  IWithAuthProps;

export class HeaderContainer extends React.Component<IHeaderContainerProps> {
  private navigationHeaderRef = React.createRef<NavigationHeader>();

  public render() {
    const { isLocalizationVisible, menuContainer, popupContainer, user, t } =
      this.props;

    const memberGroup = getUserGroup();
    const isPremiumLEGACY = doesUserHaveEntitlements(
      PremiumEntitlements.Premium_Site_Access
    );
    const isAddonPerformer =
      doesUserHaveEntitlements(AddOnEntitlements.AddOnBundle) ||
      isPremiumLEGACY;
    const isAgencySettingsFlagEnabled =
      this.props.flags[FEATURE_FLAGS.AgencySettings];
    const isSelfTapesEnabled = this.props.flags[FEATURE_FLAGS.SelfTapes];
    const isFilmmakersPhase4Enabled =
      this.props.flags[FEATURE_FLAGS.FilmmakersPhase4];

    const { lhNavBottomLinks, headerLabels, localPopUpText, mainNav } =
      getNavHeaderLinks(memberGroup, {
        isAddonPerformer,
        isAgencySettingsFlagEnabled,
        isSelfTapesEnabled,
        isFilmmakersPhase4Enabled,
      });

    const handlers: INavigationHeaderProps["handlers"] = {
      onSignOutHandler: this.handleSignOutClick,
      onClickAccountHandler: this.handleAccountClick,
      onLocalizationChangedHandler: this.handleLocalizationChange,
    };

    const mainNavData: INavigation = {
      ...mainNav,
      isActiveNavigationItem: this.isNavigationItemSelected(),
      isActiveSubNavigationItem: this.isSubNavigationItemSelected(),
    };

    return (
      <NavigationHeader
        ref={this.navigationHeaderRef}
        bottomNavLinks={lhNavBottomLinks}
        menuContainer={menuContainer}
        popupContainer={popupContainer}
        accountType={user.type}
        headerLabels={headerLabels}
        logoRedirectionLink={config.marketingSiteUrl}
        links={[]}
        signedIn
        isLocalizationVisible={isLocalizationVisible}
        countryISOCode={
          LocalizationUtils.getUserLocalizationPreference().location
        }
        handlers={handlers}
        menuClassName="c-header-section__navigation-menu"
        userName={userService.getFullName(user)}
        mainNavData={mainNavData}
        userLinkUrl={myAccountLink}
        localizationTexts={localPopUpText}
        t={t}
      />
    );
  }

  private handleLocalizationChange: LocalizationChangeHandler = (
    language,
    location
  ) => {
    this.props.setLocale(
      LocalizationUtils.getLocalizationCode(language, location)
    );
  };

  private handleSignOutClick = () => oidcLogout();

  public handleAccountClick = (): void => {
    window.location.assign(myAccountLink);
  };

  private isNavigationItemSelected() {
    return (
      item: IMainNavigationItemProps | ISubNavigationItemProps
    ): boolean => {
      return item.linkUrl.startsWith(BASE_PATH);
    };
  }

  private isSubNavigationItemSelected() {
    return (
      item: IMainNavigationItemProps | ISubNavigationItemProps
    ): boolean => {
      const currentPath = navigator.getCurrentPath();

      return Object.values(routeTesters).some(
        (tester) => tester(item.linkUrl) && tester(currentPath)
      );
    };
  }
}

const mapDispatchToProps: MapDispatchToProps<IHeaderContainerActions> = {
  showPopup,
  hidePopup,
  setLocale,
};

const mapStateToProps = (
  state: IApplicationState
): IHeaderContainerStoreProps => {
  const user = state.user.data!;

  return {
    user,
    locale: state.ui.locale,
    isLocalizationVisible: !!config?.isDev,
  };
};

export default compose(
  withAuth,
  connect(mapStateToProps, mapDispatchToProps),
  withRouter,
  withLaunchDarklyConsumerHOC(),
  withTranslation()
)(HeaderContainer) as React.ComponentType<
  React.PropsWithChildren<IContainerProps>
>;
